import React, { useState, useEffect } from 'react';
import './ApprovalPage.css';

const ApprovalPage = ({ user }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cd-requests/pending/approvals');
      const data = await response.json();
      // Ensure data is an array
      setPendingRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      setMessage('Error loading pending requests');
      setPendingRequests([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked && Array.isArray(pendingRequests)) {
      setSelectedRequests(pendingRequests.map(req => req.cd_request));
    } else {
      setSelectedRequests([]);
    }
  };

  const handleSelectRequest = (cdRequest) => {
    if (selectedRequests.includes(cdRequest)) {
      setSelectedRequests(selectedRequests.filter(id => id !== cdRequest));
    } else {
      setSelectedRequests([...selectedRequests, cdRequest]);
    }
  };

  const handleApprove = async () => {
    if (selectedRequests.length === 0) {
      setMessage('Please select at least one request');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${selectedRequests.length} request(s)?`)) {
      return;
    }

    try {
      setActionLoading(true);
      setMessage('');

      const response = await fetch('/api/approvals/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cdRequests: selectedRequests,
          approverEmail: user.email,
          approverName: user.name
        }),
      });

      const data = await response.json();

      if (data.success) {
        const successCount = Array.isArray(data.results)
          ? data.results.filter(r => r.status === 'success').length
          : selectedRequests.length;
        const failCount = selectedRequests.length - successCount;
        const failNote  = failCount > 0 ? ` (${failCount} failed — check logs)` : '';
        setMessage(`Successfully approved ${successCount} request(s)${failNote}`);
        setSelectedRequests([]);
        fetchPendingRequests();
      } else {
        setMessage('Error approving requests: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error approving requests:', error);
      setMessage('Error approving requests');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    if (selectedRequests.length === 0) {
      setMessage('Please select at least one request');
      return;
    }
    setShowRejectionModal(true);
  };

  const submitRejection = async () => {
    try {
      setActionLoading(true);
      setMessage('');
      setShowRejectionModal(false);

      const response = await fetch('/api/approvals/reject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cdRequests: selectedRequests,
          approverEmail: user.email,
          approverName: user.name,
          rejectionReason: rejectionReason
        }),
      });

      const data = await response.json();

      if (data.success) {
        const successCount = Array.isArray(data.results)
          ? data.results.filter(r => r.status === 'success').length
          : selectedRequests.length;
        const failCount = selectedRequests.length - successCount;
        const failNote  = failCount > 0 ? ` (${failCount} failed — check logs)` : '';
        setMessage(`Successfully rejected ${successCount} request(s)${failNote}`);
        setSelectedRequests([]);
        setRejectionReason('');
        fetchPendingRequests();
      } else {
        setMessage('Error rejecting requests: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error rejecting requests:', error);
      setMessage('Error rejecting requests');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading pending requests...</div>;
  }

  return (
    <div className="approval-page">
      <div className="page-header">
        <h2>Pending Approvals</h2>
        <p>Review and approve or reject CodeDisk requests</p>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {pendingRequests.length === 0 ? (
        <div className="no-requests">
          <p>No pending requests at this time</p>
        </div>
      ) : (
        <>
          <div className="action-bar">
            <div className="select-info">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedRequests.length === pendingRequests.length}
                  onChange={handleSelectAll}
                />
                Select All ({selectedRequests.length} selected)
              </label>
            </div>
            <div className="action-buttons">
              <button
                onClick={handleApprove}
                disabled={selectedRequests.length === 0 || actionLoading}
                className="btn-approve"
              >
                {actionLoading ? 'Processing...' : `Approve (${selectedRequests.length})`}
              </button>
              <button
                onClick={handleReject}
                disabled={selectedRequests.length === 0 || actionLoading}
                className="btn-reject"
              >
                Reject ({selectedRequests.length})
              </button>
            </div>
          </div>

          <div className="requests-table">
            <table>
              <thead>
                <tr>
                  <th width="50px">
                    <input
                      type="checkbox"
                      checked={selectedRequests.length === pendingRequests.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Request #</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Scan Length</th>
                  <th>Margin</th>
                  <th>INC</th>
                  <th>MLS</th>
                  <th>Count</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {pendingRequests.map(request => (
                  <tr key={request.cd_request}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRequests.includes(request.cd_request)}
                        onChange={() => handleSelectRequest(request.cd_request)}
                      />
                    </td>
                    <td><strong>{request.cd_request}</strong></td>
                    <td>{request.customer_name}</td>
                    <td>{request.customer_email}</td>
                    <td>{request.scan_length} mm</td>
                    <td>{request.margin_per_side} mm</td>
                    <td>{request.inc} mm</td>
                    <td>{request.mls} mm</td>
                    <td>{request.cd_count}</td>
                    <td>{new Date(request.creation_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showRejectionModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reject Requests</h3>
            <p>Please provide a reason for rejection:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows="4"
            />
            <div className="modal-buttons">
              <button onClick={() => setShowRejectionModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={submitRejection} className="btn-confirm">
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalPage;

